import { useMemo, useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import { useDomesticV1CartDayuseTimeLinesQuery } from '~/app/react-query/domestic/cart/v1'
import type { PostCartDayuseTimeLinesDto } from '~/api/routes/domestic/cart/controller/dto'
import { DomesticCartDayuseTimeLine } from '~/api/routes/domestic/cart/controller/type'
/**
 * 대실 시간 정보 조회에 필요한 파라미터
 */
export interface UseDayuseTimelinesParams {
  optionId: string
  checkInDateYmd: string
  checkOutDateYmd: string
  checkInTime: string
  roomTypeId: number
  ratePlanId: number | undefined
  ratePlanVersion: number | undefined
  adults: number
  childAges: number[] | undefined
  enabled: boolean
}

/**
 * 장바구니 대실 시간 정보를 조회하는 hook
 * @param optionId 옵션 ID
 * @param params 대실 시간 정보 조회에 필요한 파라미터
 * @param enabled 쿼리 실행 여부 (기본값: false)
 */
export const useDayuseTimelines = ({
  optionId,
  checkInDateYmd,
  checkOutDateYmd,
  checkInTime,
  roomTypeId,
  ratePlanId,
  ratePlanVersion,
  adults,
  childAges,
  enabled,
}: UseDayuseTimelinesParams) => {
  const [selectedTimeLine, setSelectedTimeLine] = useState<DomesticCartDayuseTimeLine | undefined>(
    undefined
  )
  // params 정보를 기반으로 API 요청 body 구성
  const requestBody: PostCartDayuseTimeLinesDto['body'] = useMemo(
    () => ({
      checkInDate: checkInDateYmd,
      checkOutDate: checkOutDateYmd,
      checkInTime,
      ratePlanId: ratePlanId ?? undefined,
      ratePlanVersion: ratePlanVersion ?? undefined,
      roomTypeId,
      persons: {
        adultPax: adults,
        childAges: childAges,
      },
    }),
    [
      checkInDateYmd,
      checkOutDateYmd,
      checkInTime,
      ratePlanId,
      ratePlanVersion,
      roomTypeId,
      adults,
      childAges,
    ]
  )

  const { data: dayuseTimeInfo } = useDomesticV1CartDayuseTimeLinesQuery(
    optionId,
    requestBody,
    enabled
  )
  const { timeLines = [], maxUseTime = 0, lastCheckInTime = '' } = dayuseTimeInfo ?? {}

  const onSelectTimeLine = (timeLine: DomesticCartDayuseTimeLine) => {
    setSelectedTimeLine(timeLine)
  }

  // requestBody의 checkInAt 문자열 생성 (안정적인 의존성)
  const checkInAtKey = useMemo(() => {
    if (!requestBody) return undefined
    return `${requestBody.checkInDate} ${requestBody.checkInTime}`
  }, [requestBody])

  // timeLines와 requestBody를 기반으로 찾을 타임라인 계산
  useEffect(() => {
    if (timeLines.length === 0 || !checkInAtKey) return undefined
    const foundTimeLine = timeLines.find(timeLine => timeLine.checkInAt === checkInAtKey)
    setSelectedTimeLine(foundTimeLine)
  }, [timeLines, checkInAtKey])

  /* 대실 마감 시간으로 남은 시간 계산(hour, minute) */
  const { remainingHours, remainingMinutes } = useMemo(() => {
    if (!selectedTimeLine?.checkInAt || !selectedTimeLine?.checkOutAt) {
      return {
        remainingHours: 0,
        remainingMinutes: 0,
      }
    }

    const diffMinutes = DateTime.fromFormat(selectedTimeLine.checkOutAt, 'yyyy-MM-dd HH:mm').diff(
      DateTime.fromFormat(selectedTimeLine.checkInAt, 'yyyy-MM-dd HH:mm'),
      ['minutes']
    ).minutes

    if (maxUseTime && lastCheckInTime && maxUseTime > diffMinutes) {
      return {
        remainingHours: Math.floor(diffMinutes / 60),
        remainingMinutes: diffMinutes % 60,
      }
    }
    return {
      remainingHours: 0,
      remainingMinutes: 0,
    }
  }, [maxUseTime, lastCheckInTime, selectedTimeLine])

  return {
    timeLines,
    maxUseTime,
    lastCheckInTime,
    selectedTimeLine,
    onSelectTimeLine,
    remainingHours,
    remainingMinutes,
  }
}

export default useDayuseTimelines
